/*
 #*===================================================================
 #*
 #* Licensed Materials - Property of GameTimeFootage Inc.
 #*
 #* Copyright NextPro 2018. All Rights Reserved.
 #*
 #*===================================================================
 */

import { forkJoin, Observable, of, throwError as observableThrowError } from 'rxjs';
import { Injectable } from '@angular/core';
import { HttpClient, HttpRequest } from '@angular/common/http';
import { catchError, map, mergeMap, switchMap } from 'rxjs/operators';
import { environment } from 'environments/environment';
import { Constants } from 'app/shared/models/constants.model';
import { ServerResponseModel } from 'app/shared/models/serverResponse.model';
import { HttpHelpersService } from 'app/shared/services/http-helpers.service';
import { UtilsService } from 'app/shared/services/utils.service';
import dayjs from 'dayjs';

@Injectable({ providedIn: 'root' })
export class AssetsService {
  constructor(private httpClient: HttpClient, private utilsService: UtilsService) {}

  addClipAsset(assetData: any): Observable<any> {
    let url: string = assetData.url;
    const { startTime, duration } = (() => {
      const split: string[] = url.split('/');

      return {
        startTime: +split[split.length - 3],
        duration: +split[split.length - 2],
      };
    })();

    url = url
      .replace(`/${startTime}`, '')
      .replace(`/${duration}`, '')
      .replace(/.+video_assets\//gi, 'video_assets/');

    const input: string = url;
    const output: string =
      'video_assets/_clips/' +
      url
        .replace('video_assets/', '')
        .replace(/(_480p_30fps|_720p_30fps|_720p_60fps|)\.mp4/, `_${startTime}_${duration}$1.mp4`);

    const payload = {
      inputPath: input,
      outputPath: output,
      startTime,
      duration,
      thumbnailPath: output.replace(/mp4$/, 'jpg'),
    };

    return this.httpClient
      .post<string>(`${environment.transcoder}/clipping`, payload, {
        responseType: 'text' as 'json',
      })
      .pipe(
        mergeMap(() => {
          return this.httpClient.post<any>(`${environment.apiHostname}/v1/assets/video`, {
            ...assetData,
            url: `${environment.fastly.main}/${payload.outputPath.replace('video_assets/', '')}`,
            urlThumb: `${environment.fastly.main}/${payload.thumbnailPath.replace('video_assets/', '')}`,
          });
        }),
        map((response) => response.data),
        catchError((error: any) => observableThrowError(error.json()))
      );
  }

  storeAsset(file: any, uploadToBucketOnly: boolean, skipResize?: boolean, folderName?: string): Observable<any> {
    const fd = new FormData();
    const params: any = {};
    fd.append('file', file, file.name);
    if (uploadToBucketOnly) {
      params.uploadToBucketOnly = uploadToBucketOnly.toString();
    }
    if (skipResize) {
      params.skipResize = skipResize;
    }
    if (folderName) {
      params.folderName = environment.webAssetsFolderName.concat('/', folderName);
    }
    params.bucketName = environment.bucketName;

    return this.httpClient
      .post<any>(`${environment.apiHostname}/v1/assets`, fd, { params })
      .pipe(map((response) => response.data))
      .pipe(catchError((error: any) => observableThrowError(error.json())));
  }

  addVideoAsset(assetData: any): Observable<any> {
    return this.httpClient
      .post<any>(`${environment.apiHostname}/v1/assets/video`, assetData)
      .pipe(map((response) => response.data))
      .pipe(catchError((error: any) => observableThrowError(error.json())));
  }

  deleteAsset(assetId: number) {
    return HttpHelpersService.handleOperationResponse(
      this.httpClient.delete(`${environment.apiHostname}/v1/assets/${assetId}`)
    );
  }

  getAsset(assetId: number) {
    return this.getResponse(this.httpClient.get(`${environment.apiHostname}/v1/assets/${assetId.toString()}`));
  }

  getVideoAsset(assetId: number) {
    return this.getResponse(this.httpClient.get(`${environment.apiHostname}/v1/assets/video/${assetId.toString()}`));
  }

  downloadAsset(assetId: number): Observable<any> {
    return this.httpClient
      .put<any>(`${environment.apiHostname}/v1/user-actions/download/asset/${assetId.toString()}`, {})
      .pipe(map((likes) => likes.data))
      .pipe(catchError((error: any) => error));
  }

  downloadBlob(source: string): Observable<any> {
    return this.httpClient.get(source, { responseType: 'blob', reportProgress: true });
  }

  getPagedVideos(filter: any) {
    const serviceCallsObservableArray = [];
    const take = filter.take / filter.sportIds.length;
    const skip = filter.skip / filter.sportIds.length;
    filter.sportIds.forEach((sportId) => {
      let metadataPart = '';
      if (filter.metadata) {
        metadataPart = `&metadataField=${filter.metadata}`;
      }
      serviceCallsObservableArray.push(
        this.httpClient.get(
          `${environment.apiHostname}/v1/assets/list/${sportId}?take=${take}&skip=${skip}&type=${filter.type}&assetType=${filter.assetType}${metadataPart}`
        )
      );
    });
    return forkJoin(serviceCallsObservableArray).pipe(
      map((responses: any[]) => {
        let videos = [];
        for (const response of responses) {
          const assets: any[] = response.data;
          videos = videos.concat(assets);
        }
        if (
          filter.type === Constants.SPORTS_FILTER_TYPE.TRENDING ||
          filter.type === Constants.SPORTS_FILTER_TYPE.MOST_VIEWED ||
          filter.type === Constants.SPORTS_FILTER_TYPE.POPULAR
        ) {
          videos.sort((a, b) => b.views - a.views);
        }
        if (filter.type === Constants.SPORTS_FILTER_TYPE.ALL) {
          videos.sort((a, b) => b.id - a.id);
        }
        return videos;
      })
    );
  }

  toggleVideoPrivateStatus(assetId: number, privateStatus: boolean, privateForUserId?: number) {
    let url: string;
    if (privateStatus === true) {
      url = `${environment.apiHostname}/v1/users/set-private/asset/${assetId}`;
    } else {
      url = `${environment.apiHostname}/v1/users/set-public/asset/${assetId}`;
    }

    // if we get a privateForUserId then set that user. If not set it for the current user
    if (privateForUserId) {
      url += `/user/${privateForUserId}`;
    }

    return this.httpClient.post(url, {});
  }

  setCustomAsset(assetId: number, userId: number) {
    return this.httpClient
      .post(`${environment.apiHostname}/v1/users/set-custom/user/${userId}/asset/${assetId}`, {})
      .pipe(map((response: ServerResponseModel) => response.data))
      .pipe(catchError((error: any) => observableThrowError(error.json())));
  }

  removeCustomAsset(assetId: number, userId: number) {
    return this.httpClient
      .delete(`${environment.apiHostname}/v1/users/set-custom/user/${userId}/asset/${assetId}`, {})
      .pipe(map((response: ServerResponseModel) => response.data))
      .pipe(catchError((error: any) => observableThrowError(error.json())));
  }

  getCustomAssets(userId: number) {
    return this.getResponse(this.httpClient.get(`${environment.apiHostname}/v1/users/${userId}/custom/assets`));
  }

  getClipsForUsers(userIds: number[]) {
    return this.getResponse(this.httpClient.post(`${environment.apiHostname}/v1/assets/clips`, { userIds }));
  }

  getLibraryHighlightsForUsers(userIds: number[]) {
    return this.getResponse(this.httpClient.post(`${environment.apiHostname}/v1/assets/hr-videos`, { userIds }));
  }

  updateAsset(payload) {
    let slimPayload = payload;
    if (payload.user) {
      slimPayload = { ...payload, user: { id: payload.user.id } };
    }
    if (slimPayload.published !== undefined && slimPayload.published !== null) {
      slimPayload.published = Boolean(slimPayload.published);
    }
    return this.httpClient
      .put(`${environment.apiHostname}/v1/assets/${payload.id}`, slimPayload)
      .pipe(map((response: ServerResponseModel) => response.data))
      .pipe(catchError((error: any) => observableThrowError(error.json())));
  }

  getRandomSportVideos(assetType: string, sportId: number) {
    const params: any = {
      type: 'random',
      assetType,
      take: 40,
      skip: 0,
    };
    return this.httpClient
      .get(`${environment.apiHostname}/v1/assets/list/${sportId}`, {
        params,
      })
      .pipe(map((response: ServerResponseModel) => response.data))
      .pipe(catchError((error: any) => observableThrowError(error.json())));
  }

  searchAssets(assetType: string, metadataField?: any): Observable<any> {
    const params: any = { take: 0, skip: 0, type: assetType };
    if (metadataField) {
      params.metadataField = metadataField;
    }
    return this.httpClient
      .post<any>(`${environment.apiHostname}/v1/assets/search`, params)
      .pipe(map((response) => response.data))
      .pipe(catchError((error: any) => observableThrowError(error.json())));
  }

  getVideoThumb(video): Observable<any> {
    let bitrate = null;
    if (video.urlThumb) {
      return of(video.urlThumb);
    }
    let thumbnailSourcePath = video.url.split('.mp4')[0].split(Constants.GS_ASSET_HOSTNAME + '/')[1];
    const lastDirectoryIndex = thumbnailSourcePath.lastIndexOf('/');
    thumbnailSourcePath =
      thumbnailSourcePath.substring(0, lastDirectoryIndex) +
      '/thumbnails/' +
      thumbnailSourcePath.substring(lastDirectoryIndex + 1);

    if (thumbnailSourcePath.endsWith(Constants.VIDEO_QUALITY.q720_30)) {
      bitrate = Constants.VIDEO_QUALITY.q720_30;
    }
    if (thumbnailSourcePath.endsWith(Constants.VIDEO_QUALITY.q720_60)) {
      bitrate = Constants.VIDEO_QUALITY.q720_60;
    }

    if (bitrate) {
      return of(
        Constants.GS_ASSET_HOSTNAME + '/' + encodeURIComponent(thumbnailSourcePath) + `${bitrate}/thumbnail.jpg`
      );
    } else {
      return this.utilsService.checkAvailableBitrates(video.url).pipe(
        map((bitrates: any) => {
          if (bitrates?.data?._720p_30fps) {
            bitrate = Constants.VIDEO_QUALITY.q720_30;
          } else if (bitrates?.data?._720p_60fps) {
            bitrate = Constants.VIDEO_QUALITY.q720_60;
          }
          return (
            Constants.GS_ASSET_HOSTNAME + '/' + encodeURIComponent(thumbnailSourcePath) + `${bitrate}/thumbnail.jpg`
          );
        })
      );
    }
  }

  uploadFileToBucket(filePath: string, file: any, bucketName: string): Observable<any> {
    return this.getSignedUrl(filePath, bucketName).pipe(
      switchMap((signedUrl) => {
        const uploadReq = new HttpRequest('PUT', `${signedUrl[0]}`, file, {
          reportProgress: true,
        });
        return this.httpClient.request(uploadReq);
      })
    );
  }

  incrementViews(assetId: number): Observable<any> {
    return this.httpClient.post(`${environment.apiHostname}/v1/assets/increment/${assetId}`, {}).pipe(
      map((response: ServerResponseModel) => response.data),
      catchError((error: any) => observableThrowError(error.json()))
    );
  }

  getUrlThumb(url, is60fps = false) {
    if (!url) {
      return null;
    }

    let thumbnailSourcePath = url.split('.mp4')[0].replace(`${Constants.GS_ASSET_HOSTNAME}/`, '');

    const lastDirectoryIndex = thumbnailSourcePath.lastIndexOf('/');
    thumbnailSourcePath =
      thumbnailSourcePath.substring(0, lastDirectoryIndex) +
      '/thumbnails/' +
      thumbnailSourcePath.substring(lastDirectoryIndex + 1);

    const fpsSuffix = is60fps ? '_720p_60fps' : '_720p_30fps';
    let urlThumb = `${Constants.GS_ASSET_HOSTNAME}/${encodeURIComponent(
      thumbnailSourcePath
    )}${fpsSuffix}/thumbnail.jpg`;

    if (urlThumb) {
      urlThumb = urlThumb
        .replace(Constants.GS_ASSET_HOSTNAME + '/video_assets', environment.fastly.main)
        .replace('%2F', '/')
        .replace(/#/g, '%23');
    }
    return urlThumb;
  }

  getEventLink(eventName, eventId, eventDate, sportName, isAdminUser = false) {
    const isPastEvent = dayjs(eventDate).isBefore(dayjs());
    let eventPage = isPastEvent ? 'clips' : 'featured';
    if (isAdminUser) {
      eventPage = 'fullgames';
    }

    if (sportName && eventName && eventId) {
      return [
        '/',
        this.utilsService.getSlugForName(sportName),
        'events',
        this.utilsService.getSlugForName(eventName),
        eventId,
        eventPage,
      ];
    }
    if (eventId) {
      return ['/', 'events', eventId, eventPage];
    }
    return [''];
  }

  private getSignedUrl(filePath: string, bucketName: string): Observable<any> {
    return this.httpClient
      .get<any>(`${environment.apiHostname}/v1/buckets/${bucketName}/upload-signed-uri`, {
        params: { origin: location.protocol + '//' + location.host, path: filePath },
      })
      .pipe(
        map((response) => response.data),
        catchError((error: any) => observableThrowError(error.json()))
      );
  }

  private getResponse(observable: Observable<any>) {
    return observable.pipe(
      map((response: any) => {
        if (response.result.success) {
          return response.data;
        } else {
          throw new Error(response.result.message);
        }
      })
    );
  }
}
